'use client';

/* Lanceur de chatbot flottant — présent sur tout l'outil. Self-service + technicien Myopla + recherche KB. */
import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MessageCircle, X, Send, Bot, Headset, KeyRound, BookOpen, User } from 'lucide-react';

interface KbSource { n: number; title: string; lot: string; category: string; score: number; }
interface Msg { role: 'user' | 'bot' | 'tech' | 'system'; content: string; action?: string | null; agent?: string | null; sources?: KbSource[]; }

const SUGGEST = ["J'ai oublié mon mot de passe", 'Ma caisse Toshiba est figée', "Je n'ai plus accès à GOLD", 'Mon imprimante est en panne'];

export function ChatLauncher() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([
    { role: 'bot', content: "Bonjour 👋 Assistant self-service du Groupe Casino. Décrivez votre besoin — je cherche dans la base de connaissances et vous aide, ou je vous mets en relation avec un technicien Myopla." },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'bot' | 'technician'>('bot');
  const [resetting, setResetting] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading, open]);

  const history = () => messages.filter(m => ['user', 'bot', 'tech'].includes(m.role)).map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.content }));

  async function ask(text: string) {
    if (!text.trim() || loading) return;
    setInput(''); setMessages(m => [...m, { role: 'user', content: text }]); setLoading(true);
    try {
      const r = await fetch('/api/portail/chat', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ message: text, mode, history: history() }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.error || 'Erreur');
      setMessages(m => [...m, { role: mode === 'technician' ? 'tech' : 'bot', content: d.answer, action: d.action, agent: d.agent, sources: d.sources }]);
    } catch (e: any) { setMessages(m => [...m, { role: 'bot', content: `⚠️ ${e.message}` }]); }
    finally { setLoading(false); }
  }
  async function reset() {
    setResetting(true);
    try {
      const r = await fetch('/api/portail/reset-password', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' });
      const d = await r.json();
      setMessages(m => [...m, { role: 'bot', content: d.success ? `✅ **Mot de passe réinitialisé en self-service.** Temporaire : **${d.tempPassword}**. Ticket **${d.ref}** clôturé automatiquement (*ticket machine*, sans intervention humaine).` : `⚠️ ${d.error}` }]);
    } finally { setResetting(false); }
  }
  function technician() {
    if (mode === 'technician') return;
    setMode('technician');
    setMessages(m => [...m, { role: 'system', content: 'Mise en relation avec un technicien Myopla…' }, { role: 'tech', content: "Bonjour, **Karim** de l'équipe Myopla 👨‍🔧 Je prends la suite. Pouvez-vous me préciser le magasin et le souci ?", agent: 'Karim — Technicien Myopla' }]);
  }

  return (
    <>
      {/* Bulle flottante */}
      <button onClick={() => setOpen(o => !o)} aria-label="Ouvrir l'assistant"
        className="fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full bg-gradient-to-br from-[#E2001A] to-[#B0000F] text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform">
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-7 w-7" />}
      </button>
      {!open && (
        <div className="fixed bottom-7 right-20 z-[60] bg-white border rounded-full px-3 py-1 text-xs shadow-md hidden sm:block">Besoin d'aide ? 💬</div>
      )}

      {/* Panneau */}
      {open && (
        <div className="fixed bottom-24 right-5 z-[60] w-[380px] max-w-[92vw] h-[70vh] bg-card border rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          <div className="bg-gradient-to-r from-[#E2001A] to-[#B0000F] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-white/15 p-1.5">{mode === 'technician' ? <Headset className="h-4 w-4" /> : <Bot className="h-4 w-4" />}</div>
              <div>
                <div className="text-sm font-bold leading-tight">Assistance Groupe Casino</div>
                <div className="text-[10px] text-white/80">{mode === 'technician' ? 'ChatLive — Technicien Myopla' : 'Chatbot self-service · 24/7'}</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={technician} disabled={mode === 'technician'} title="Discuter avec un technicien Myopla" className="rounded-md bg-white/15 hover:bg-white/25 px-2 py-1 disabled:opacity-60"><Headset className="h-4 w-4" /></button>
              <button onClick={() => setOpen(false)}><X className="h-4 w-4" /></button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-background/40">
            {messages.map((m, i) => {
              if (m.role === 'system') return <div key={i} className="text-center text-[10px] text-muted-foreground py-1">— {m.content} —</div>;
              const isUser = m.role === 'user';
              return (
                <div key={i} className={isUser ? 'flex justify-end' : 'flex justify-start gap-1.5'}>
                  {!isUser && <div className={`shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-white ${m.role === 'tech' ? 'bg-emerald-600' : 'bg-primary'}`}>{m.role === 'tech' ? <Headset className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}</div>}
                  <div className={isUser ? 'max-w-[80%] rounded-2xl rounded-br-sm bg-primary text-white px-3 py-1.5 text-xs' : 'max-w-[85%] rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-xs prose prose-sm max-w-none'}>
                    {m.agent && <div className="text-[10px] font-semibold text-emerald-700 mb-0.5">{m.agent}</div>}
                    {isUser ? m.content : <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>}
                    {m.action === 'password_reset' && (
                      <button onClick={reset} disabled={resetting} className="not-prose mt-1.5 inline-flex items-center gap-1 rounded-lg bg-primary text-white px-2.5 py-1 text-[11px] font-medium disabled:opacity-50"><KeyRound className="h-3 w-3" /> {resetting ? '…' : 'Réinitialiser mon mot de passe'}</button>
                    )}
                    {!isUser && m.sources && m.sources.length > 0 && (
                      <div className="not-prose mt-1.5 pt-1.5 border-t border-black/5">
                        <div className="text-[9px] font-semibold text-muted-foreground mb-1 flex items-center gap-1"><BookOpen className="h-2.5 w-2.5" /> Base de connaissances consultée</div>
                        <div className="flex flex-wrap gap-1">{m.sources.map(s => <span key={s.n} className="rounded-full bg-background border px-1.5 py-0.5 text-[9px]">{s.title.length > 34 ? s.title.slice(0, 34) + '…' : s.title} <span className="text-muted-foreground">· {s.score}%</span></span>)}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {loading && <div className="text-[10px] text-muted-foreground pl-1">{mode === 'technician' ? 'Karim écrit…' : 'Recherche dans la base de connaissances…'}</div>}
            <div ref={endRef} />
          </div>

          {messages.length <= 1 && (
            <div className="px-3 pb-1 flex flex-wrap gap-1">{SUGGEST.map(s => <button key={s} onClick={() => ask(s)} className="rounded-full border px-2 py-1 text-[10px] hover:border-primary hover:bg-primary/5">{s}</button>)}</div>
          )}
          <form onSubmit={e => { e.preventDefault(); ask(input); }} className="border-t p-2 flex gap-1.5">
            <input value={input} onChange={e => setInput(e.target.value)} disabled={loading} placeholder={mode === 'technician' ? 'Écrire au technicien…' : 'Votre demande…'} className="flex-1 rounded-lg border px-2.5 py-1.5 text-xs bg-background outline-none focus:border-primary" />
            <button type="submit" disabled={loading || !input.trim()} className="rounded-lg bg-primary text-white px-3 py-1.5 disabled:opacity-50"><Send className="h-3.5 w-3.5" /></button>
          </form>
        </div>
      )}
    </>
  );
}

export default ChatLauncher;
